import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useRestaurant } from "../context/RestaurantContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ChevronLeft, Menu, LogOut, User, Home, Package, Truck, ShoppingCart, ClipboardList, History, Users, Store, Layers, Bell } from "lucide-react";

export default function AppLayout({ children, title, showBack = false, hideTitle = false, showRestaurantSelector = false }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { restaurants, currentRestaurant, selectRestaurant, clearRestaurant, getCombinedOption } = useRestaurant();
  const isAdmin = user?.role === "admin";

  const handleLogout = () => {
    logout();
    clearRestaurant();
    navigate("/login");
  };

  const handleRestaurantChange = (restaurantId) => {
    if (restaurantId === "combined") {
      selectRestaurant(getCombinedOption());
    } else {
      const restaurant = restaurants.find(r => r.id === restaurantId);
      if (restaurant) {
        selectRestaurant(restaurant);
      }
    }
  };

  const navItems = [
    { href: "/", label: "Dashboard", icon: Home },
    ...(isAdmin ? [
      { href: "/users", label: "Users", icon: Users },
      { href: "/restaurants", label: "Restaurants", icon: Store },
      { href: "/vendors", label: "Vendors", icon: Truck },
      { href: "/items", label: "Items", icon: Package },
      { href: "/pending-orders", label: "Pending Orders", icon: ClipboardList },
    ] : []),
    { href: "/create-order", label: "Create Order", icon: ShoppingCart },
    { href: "/order-history", label: "Order History", icon: History },
    { href: "/notifications", label: "Notifications", icon: Bell },
  ];

  return (
    <div className="min-h-screen bg-bone w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-stone-200 shadow-sm w-full">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center">
          {/* Left side - Back button and Title */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {showBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="h-9 w-9 -ml-2 flex-shrink-0"
                data-testid="back-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            )}
            {!hideTitle && title && (
              <h1 className="font-semibold text-stone-900 truncate">{title}</h1>
            )}
          </div>

          {/* Center - Restaurant Selector (only on Dashboard) */}
          {showRestaurantSelector && restaurants.length > 0 && currentRestaurant && (
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <Select value={currentRestaurant.id} onValueChange={handleRestaurantChange}>
                <SelectTrigger className={`h-10 w-auto max-w-[220px] text-sm font-semibold ${currentRestaurant.id === "combined" ? "border-purple-400 bg-purple-50 text-purple-900" : "border-kale-300 bg-kale-50 text-kale-900"}`} data-testid="restaurant-selector">
                  {currentRestaurant.id === "combined" ? (
                    <Layers className="w-4 h-4 mr-2 flex-shrink-0" />
                  ) : (
                    <Store className="w-4 h-4 mr-2 flex-shrink-0" />
                  )}
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Combined Order Option */}
                  <SelectItem value="combined" className="py-2">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-purple-600" />
                      <span className="text-purple-700 font-medium">Combined Order</span>
                    </div>
                  </SelectItem>
                  <div className="border-t border-stone-200 my-1" />
                  {restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id} className="py-2">
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Right side - Menu */}
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="menu-button">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b border-stone-100">
                  <p className="text-sm font-medium text-stone-900">{user?.name}</p>
                  <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
                </div>
                
                {navItems.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link to={item.href} className="flex items-center gap-2 cursor-pointer" data-testid={`menu-${item.href.replace('/', '') || 'home'}`}>
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-safe">
        {children}
      </main>
    </div>
  );
}
